/**
 * useSupplierPhotoZip — orchestrates supplier-photo ZIP download.
 *
 * Flow:
 * 1. start(selector) → POSTs supplierPhotoDownload.manifest via auth.callWithAuth
 * 2. On manifest success, registers beforeunload and streams the ZIP to disk via
 *    showSaveFilePicker (Chrome/Edge) or StreamSaver.js fallback (Firefox/Safari)
 * 3. ZIP entries: the server-provided photo filename (<RW>_<supplier>.ext) at archive root;
 *    duplicate filenames across the whole manifest are auto-suffixed (name (2).ext)
 * 4. Each R2 fetch wrapped with AbortSignal.timeout(60 s); retried 2× on failure
 * 5. Skipped filenames surfaced as toast.error at the end
 *
 * Unlike useBulkDownloadSpecs there is NO pre-download confirm dialog and NO in-ZIP
 * manifest.json — start() streams directly on manifest success.
 *
 * Covers REQ-9.4.
 */
import { useState, useRef, useCallback } from 'react';
import { useRouteContext } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { supplierPhotoDownload } from '@ooh/contracts';
import type { SupplierPhotoManifestRequest, SupplierPhotoManifestResponse } from '@ooh/contracts';
import { toast } from 'sonner';

// showSaveFilePicker is not yet part of TypeScript's standard DOM lib types.
// Augment Window once here so call sites stay type-safe without inline casts.
declare global {
  interface Window {
    showSaveFilePicker?: (options: {
      suggestedName?: string;
      types?: { description: string; accept: Record<string, string[]> }[];
    }) => Promise<FileSystemFileHandle>;
  }
}

// ── Constants ─────────────────────────────────────────────────────────────────

const MAX_RETRIES = 2; // 3 total attempts (initial + 2 retries)
const FETCH_TIMEOUT_MS = 60_000;
const ZIP_FILENAME = 'photos.zip';

// ── Feature detection ─────────────────────────────────────────────────────────

/**
 * Returns the File System Access API's showSaveFilePicker when it is available
 * and not blocked by an iframe cross-origin context, otherwise null.
 * See: https://developer.mozilla.org/en-US/docs/Web/API/Window/showSaveFilePicker
 */
function getNativeFileSavePicker(): NonNullable<Window['showSaveFilePicker']> | null {
  if (!window.showSaveFilePicker) return null;
  try {
    return window.self === window.top ? window.showSaveFilePicker : null;
  } catch {
    // Cross-origin iframe — access to window.top throws
    return null;
  }
}

// ── Duplicate-filename deduplication ─────────────────────────────────────────

/**
 * Given a base filename and a seen-names map, returns a unique filename by
 * appending ` (N)` before the extension when a collision is detected.
 * Mutates the map to track the new name.
 */
function uniqueFilename(base: string, seen: Map<string, number>): string {
  if (!seen.has(base)) {
    seen.set(base, 1);
    return base;
  }
  const count = seen.get(base)! + 1;
  seen.set(base, count);

  const lastDot = base.lastIndexOf('.');
  if (lastDot === -1) {
    return `${base} (${count})`;
  }
  const name = base.slice(0, lastDot);
  const ext = base.slice(lastDot); // includes the dot
  return `${name} (${count})${ext}`;
}

// ── Retry-wrapped R2 fetch ────────────────────────────────────────────────────

/**
 * Fetches a presigned R2 URL with a 60-second timeout, retrying up to MAX_RETRIES
 * times on failure. R2 presigned URLs are fetched directly from the browser —
 * this is intentional per the architecture (bytes do not flow through Railway).
 */
async function fetchWithRetry(url: string): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      // R2 presigned URLs — raw fetch is permitted here; bytes never touch Railway
      // eslint-disable-next-line no-restricted-globals
      const res = await fetch(url, { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res;
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
}

// ── ZIP entry builder ─────────────────────────────────────────────────────────

/**
 * Fetches every photo across the manifest, applying global duplicate-filename
 * deduplication (one shared seen-names map). Returns fetched entries and appends
 * any skipped filenames to the provided array.
 */
async function fetchPhotoEntries(
  manifest: SupplierPhotoManifestResponse,
  skippedFilenames: string[],
): Promise<Array<{ name: string; input: Response }>> {
  const seenNames = new Map<string, number>();
  const entries: Array<{ name: string; input: Response }> = [];
  for (const unit of manifest.units) {
    for (const photo of unit.photos) {
      const entryName = uniqueFilename(photo.filename, seenNames);
      try {
        const res = await fetchWithRetry(photo.downloadUrl);
        entries.push({ name: entryName, input: res });
      } catch (err) {
        console.error('[supplier-photo-zip] file fetch failed', photo.filename, err);
        skippedFilenames.push(photo.filename);
      }
    }
  }
  return entries;
}

// ── ZIP streaming pipeline ─────────────────────────────────────────────────────

const ZIP_ACCEPT = { 'application/zip': ['.zip'] };

/**
 * Streams the manifest's photos into one ZIP and writes it to disk via the native
 * file picker (Chrome/Edge) or StreamSaver.js (Firefox/Safari). Returns the list of
 * filenames skipped after exhausting retries. Returns null when the user cancels the
 * Save As dialog or the ZIP stream body is unavailable (caller should not toast skips).
 */
async function streamManifestZip(
  manifest: SupplierPhotoManifestResponse,
): Promise<string[] | null> {
  const [{ downloadZip }, streamsaverModule] = await Promise.all([
    import('client-zip'),
    import('streamsaver'),
  ]);
  const streamsaver = streamsaverModule.default;
  streamsaver.mitm = '/mitm.html';

  const skippedFilenames: string[] = [];
  const fileEntries = await fetchPhotoEntries(manifest, skippedFilenames);

  const zipResponse = downloadZip(fileEntries);
  if (!zipResponse.body) {
    console.error('[supplier-photo-zip] ZIP stream body is null');
    return null;
  }

  const savePicker = getNativeFileSavePicker();
  try {
    if (savePicker) {
      const fileHandle = await savePicker({
        suggestedName: ZIP_FILENAME,
        types: [{ description: 'ZIP archive', accept: ZIP_ACCEPT }],
      });
      const writable = await fileHandle.createWritable();
      await zipResponse.body.pipeTo(writable);
    } else {
      await zipResponse.body.pipeTo(streamsaver.createWriteStream(ZIP_FILENAME));
    }
  } catch (err) {
    // AbortError = user dismissed the Save As dialog — silently ignore
    if (err instanceof DOMException && err.name === 'AbortError') return null;
    throw err;
  }

  return skippedFilenames;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export interface UseSupplierPhotoZipResult {
  /** Fetch the manifest for the selector then stream the ZIP to disk. */
  start: (selector: SupplierPhotoManifestRequest) => Promise<void>;
  /** Whether ZIP streaming is currently in progress. */
  isStreaming: boolean;
}

export function useSupplierPhotoZip(): UseSupplierPhotoZipResult {
  const { auth } = useRouteContext({ from: '__root__' });
  const { t } = useTranslation('inventory');

  const [isStreaming, setIsStreaming] = useState(false);

  // Stable ref holding the beforeunload handler so removal is guaranteed even
  // if the component re-renders between register and remove.
  const beforeunloadRef = useRef<((e: BeforeUnloadEvent) => void) | null>(null);

  const cleanupBeforeunload = useCallback(() => {
    if (beforeunloadRef.current) {
      window.removeEventListener('beforeunload', beforeunloadRef.current);
      beforeunloadRef.current = null;
    }
  }, []);

  const start = useCallback(
    async (selector: SupplierPhotoManifestRequest): Promise<void> => {
      // Fetch the manifest first — on failure, toast and bail BEFORE registering
      // any beforeunload listener or starting a stream.
      let manifest: SupplierPhotoManifestResponse;
      try {
        const response = await auth.callWithAuth(supplierPhotoDownload.manifest, selector);
        // callWithAuth returns unknown — the response is Zod-validated server-side;
        // the cast is safe because the API contract guarantees this shape.
        manifest = response as SupplierPhotoManifestResponse;
      } catch (err) {
        console.error('[supplier-photo-zip] manifest request failed', err);
        toast.error(t('supplierPhotoZip.toast.manifestError'));
        return;
      }

      // Register beforeunload guard — must happen before any async streaming work
      const handler = (e: BeforeUnloadEvent) => {
        e.preventDefault();
      };
      beforeunloadRef.current = handler;
      window.addEventListener('beforeunload', handler);
      setIsStreaming(true);

      try {
        const skipped = await streamManifestZip(manifest);
        if (skipped && skipped.length > 0) {
          toast.error(
            t('supplierPhotoZip.toast.skippedFiles', {
              count: skipped.length,
              files: skipped.join(', '),
            }),
          );
        }
      } catch (err) {
        console.error('[supplier-photo-zip] stream write failed', err);
        toast.error(t('supplierPhotoZip.toast.streamError'));
      } finally {
        cleanupBeforeunload();
        setIsStreaming(false);
      }
    },
    [auth, t, cleanupBeforeunload],
  );

  return { start, isStreaming };
}
